import { refreshSessionIfExpiringSoon } from './client';

/**
 * Session keepalive · proactive token refresh on tab wake / window focus.
 *
 * supabase-js auto-refreshes on a background ticker, but when a tab is
 * backgrounded (or the laptop sleeps) the ticker is throttled/paused, so the
 * access token can lapse. On wake, the debounced progress-sync flush
 * (`lib/storage-backend/progress-sync.ts`) fires a burst of PATCHes that each
 * hit one 401 → reactive refresh → retry. That recovers fine but spews a wall
 * of red 401s in the console.
 *
 * Refreshing *eagerly* on `visibilitychange` (→ visible) and window `focus`,
 * before the flush runs, keeps the token fresh so the burst never 401s in the
 * first place. The refresh is single-flight + expiry-gated in `client.ts`, so
 * these listeners are cheap to fire often (no redundant refreshes).
 */
export function installSessionKeepalive(): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') {
      void refreshSessionIfExpiringSoon();
    }
  };
  const onFocus = (): void => {
    void refreshSessionIfExpiringSoon();
  };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onFocus);

  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onFocus);
  };
}
